/**
 * Canonical Question data model.
 *
 * This is the single source of truth for the Question shape across the
 * monorepo (web, infra, seed). Any divergent or legacy types elsewhere
 * are deprecated.
 *
 * @format
 */

import type { Brand, Language } from "./config/brands";
import type { CoreRef, Paper, PaperQualification } from "./paper";
import { DraftQuestionInput } from "./zod-schema";

export type QuestionSeason = "Summer" | "Winter" | "Autumn" | "Spring";
export const QUESTION_SEASONS: readonly QuestionSeason[] = ["Summer", "Winter", "Autumn", "Spring"] as const;

/** Lifecycle order: draft -> staged -> published. Nothing publishes automatically. */
export const QUESTION_STATUSES = ["draft", "staged", "published"] as const;
export type QuestionStatus = (typeof QUESTION_STATUSES)[number];

/** Cognito group for a subject matter expert. The comparison is case-insensitive. */
export const SUBJECT_MATTER_EXPERT_GROUP = "sme";

export const isSubjectMatterExpert = (group: string | undefined): boolean =>
	group?.toLowerCase() === SUBJECT_MATTER_EXPERT_GROUP;

/**
 * True when `userId` is the person who moved this question to staged.
 * Publishing then needs a second person. A question with no staged actor
 * (a manual draft, or a record from before provenance) does not match.
 */
export const isPublishByStager = (
	question: { reviewProvenance?: { staged?: { userId: string } } },
	userId: string | undefined,
): boolean => {
	const stagedBy = question.reviewProvenance?.staged?.userId;
	return stagedBy !== undefined && userId !== undefined && stagedBy === userId;
};

/** Maximum length of a staged-question rejection reason. */
export const REJECTION_REASON_MAX_LENGTH = 1000;

export type QuestionLevel = "GCSE" | "AS-Level" | "A-Level";
export const QUESTION_LEVELS: readonly QuestionLevel[] = ["GCSE", "AS-Level", "A-Level"] as const;

export type QuestionTier = "Foundation" | "Intermediate" | "Higher";
export const QUESTION_TIERS: readonly QuestionTier[] = ["Foundation", "Intermediate", "Higher"] as const;

/**
 * Core API reference-data ids (QB2-224). These mirror the fixed option sets the
 * Core API owns — a question stores the Core id so its reference data is
 * authoritative rather than free text. The values match the Core API schemas
 * (`tier.ts`, `series.ts`, `assessment-objective.ts`) exactly; keep them in step
 * if the Core API set ever changes.
 */
export type CoreTierId = "1" | "2" | "3";
export const CORE_TIER_IDS: readonly CoreTierId[] = ["1", "2", "3"] as const;

export type CoreSeriesId = "1" | "2" | "3" | "4";
export const CORE_SERIES_IDS: readonly CoreSeriesId[] = ["1", "2", "3", "4"] as const;

export type CoreAssessmentObjectiveId = "1" | "2" | "3";
export const CORE_ASSESSMENT_OBJECTIVE_IDS: readonly CoreAssessmentObjectiveId[] = ["1", "2", "3"] as const;

/**
 * Display width of an asset block, as a percentage of the available content
 * width. Authors pick one of a fixed set so figures stay consistent across
 * questions and render predictably in the exported paper.
 */
export type QuestionAssetWidth = 25 | 50 | 75 | 100;
export const QUESTION_ASSET_WIDTHS: readonly QuestionAssetWidth[] = [25, 50, 75, 100] as const;

export interface QuestionSeries {
	/**
	 * Series season as a Core Taxonomy reference (DR11). `code` is the stable
	 * Core key (e.g. `summer`) used as the id-scheme season token; `title` is
	 * the display label. Replaces the old QuestionSeason enum string.
	 */
	season: CoreRef;
	year: number;
}

export interface QuestionAssessmentObjectives {
	ao1?: number;
	ao2?: number;
	ao3?: number;
}

export interface QuestionSpecification {
	theme?: string;
	topic?: string;
	subTopic?: string;
	/**
	 * Stable Core Taxonomy API identity for the pinned subtopic (QB2-309, per
	 * the QB2-268 contract in `docs/core-taxonomy-api-contract.md`). Both are
	 * UUIDs sourced from the Core API, additive and optional so existing
	 * records stay valid without a migration.
	 *
	 * `coreLineageId` is the subtopic's lineage id — the identity that survives
	 * an edition change, and the value a question pins to. `coreEditionId` is
	 * the edition the question was pinned against, so the origin edition is
	 * known without inferring it.
	 */
	coreLineageId?: string;
	coreEditionId?: string;
}

/** One ranked sub-topic alternative proposed by the classifier. */
export interface ClassificationAlternative {
	theme: string;
	topic: string;
	subTopic: string;
	/** Model confidence from 0 to 1 inclusive. */
	confidence: number;
}

/**
 * AI classification proposal for a draft question, written by the import
 * pipeline. Confidence is per field, from 0 to 1 inclusive. `alternatives`
 * holds up to three ranked sub-topic guesses, best first. The reviewer's
 * accepted choice lives on {@link QuestionSpecification}, not here.
 */
export interface QuestionClassificationProposal {
	theme?: string;
	topic?: string;
	subTopic?: string;
	confidence: { theme?: number; topic?: number; subTopic?: number };
	alternatives?: ClassificationAlternative[];
}

/**
 * Core API identity for the reference-data fields a question maps to (QB2-224).
 * Each value is the stable id the Core API uses as canonical identity. The
 * subject/tier/series these ids correspond to are paper-level facts resolved
 * via the question's `paperId` (QB2-223); this object stores only the Core ids,
 * not the display values. The `specification` subtopic identity stays on
 * {@link QuestionSpecification}; this object groups the flatter reference-data
 * mappings.
 *
 * Ids only — the display titles are resolved from the Core API reads, never
 * duplicated here (a stored title would go stale, like the specification free
 * text).
 */
export interface QuestionCoreMappings {
	/** Core subject id (kebab-case, e.g. `applied-science`). */
	subjectId?: string;
	/** Core subject-category id (WJEC "subject groups"). */
	subjectCategoryId?: string;
	/** Core tier id: `1` Foundation, `2` Intermediate, `3` Higher. */
	tierId?: CoreTierId;
	/** Core series (season) id: `1` Winter, `2` Spring, `3` Summer, `4` Autumn. */
	seriesId?: CoreSeriesId;
	/** Core assessment-objective ids that apply: `1` AO1, `2` AO2, `3` AO3. */
	assessmentObjectiveIds?: CoreAssessmentObjectiveId[];
}

/**
 * Captured-only metadata — populated via seed/ingest, not surfaced in the admin
 * form in this iteration. Used for downstream analytics and future filters.
 */
export interface QuestionMetadata {
	questionType?: string;
	demandMarks?: {
		low?: number;
		medium?: number;
		high?: number;
	};
	keyWord?: string;
	optional?: boolean;
	commonWithOtherTier?: string;
	seededOrDoubleMarked?: string;
	markingMethod?: "A" | "E";
}

/**
 * Localised (per-language) authoring content for a question. The question body
 * (prose + images) lives in the shared, ordered `question.contentBlocks`; this
 * per-language block only carries the fields that remain language-specific:
 * examiner comments and the mark scheme image.
 */
export interface QuestionLocalisedContent {
	/** Examiner comments for this language (optional). */
	examinersComments?: string;
	/** S3 asset key for the mark scheme image. Required to publish (Welsh for WJEC). */
	markSchemeImage?: string | null;
}

/**
 * A single ordered piece of a question's body. Authors build one shared,
 * ordered list of blocks so a question can interleave prose and images in any
 * sequence (e.g. text, image, text, image). Text blocks carry their own
 * per-language copy; asset blocks reference a single shared asset used for
 * every language.
 */
export type QuestionContentBlock = QuestionTextBlock | QuestionAssetBlock | QuestionAnswerLinesBlock;

/** Ordered prose block with per-language copy. */
export interface QuestionTextBlock {
	type: "text";
	/** English copy for this block. Required (per block that has any text) to publish. */
	en?: string;
	/** Welsh copy for this block. Required for the WJEC brand to publish. */
	cy?: string;
}

/** Ordered image block. Mirrors text blocks: a per-language image, so WJEC can
 * supply an English and a Welsh figure for the same position. */
export interface QuestionAssetBlock {
	type: "asset";
	/** English image asset key. The recommended vector (SVG) asset. */
	en?: string;
	/** Welsh image asset key. The recommended vector (SVG) asset. */
	cy?: string;
	/** English backup raster (PNG) asset key, for when the SVG reads poorly. */
	enRaster?: string;
	/** Welsh backup raster (PNG) asset key, for when the SVG reads poorly. */
	cyRaster?: string;
	/** Optional alternative text for accessibility. */
	alt?: string;
	/**
	 * Optional display width as a percentage of the available content width
	 * (25, 50, 75 or 100). When unset, each renderer keeps its own default
	 * sizing (natural size on screen, measured/vector width in the export).
	 */
	width?: QuestionAssetWidth;
}

/**
 * Ordered block of ruled answer lines, placed anywhere in the body so authors
 * can add blank answer space between prose/images on demand. Renders in the
 * export preview and the exported PDF as `lines` ruled lines.
 */
export interface QuestionAnswerLinesBlock {
	type: "answerLines";
	/** Number of ruled answer lines to render at this position. */
	lines: number;
}

/**
 * Who crossed one review gate, and when. The field diff for that gate stays
 * on {@link QuestionChangeRecord}. This object is only the actor and the time.
 */
export interface ReviewGate {
	/** User id, or a system actor such as `import-pipeline`. */
	userId: string;
	/** Email when the actor is a person. Absent for a system actor. */
	email?: string;
	/** ISO-8601 time the gate was crossed. */
	at: string;
}

/**
 * Extraction gate. `jobId` is the import job. `executionArn` is the Step
 * Functions execution, when the job has one.
 */
export interface ExtractionProvenance extends ReviewGate {
	jobId: string;
	executionArn?: string;
}

/**
 * Review actors on the question. The import job can expire. This object stays.
 * A later write of the same draft replaces `extracted`.
 */
export interface QuestionReviewProvenance {
	extracted?: ExtractionProvenance;
	staged?: ReviewGate;
	published?: ReviewGate;
}

/**
 * Canonical Question record.
 *
 * Paper-level facts (`subject`, `level`, `tier`, `series`, `date`) are NOT
 * stored here. They belong to the Paper and are resolved via {@link paperId}.
 * A question carries only its own data: identity, ribbon position, content,
 * marks, assessment objectives and specification. Use
 * {@link resolveQuestionPaper} to obtain the full paper-level view for display
 * or search.
 */
/** What the mark scheme extractor decided for one question. */
export interface ImportedMarkSchemeContent {
	prompt?: string | null;
	marks?: number | null;
	ao?: Record<string, number>;
	markCodes?: string[];
	comments?: string[];
	blocks?: (
		| { type: "text"; en: string; cy?: string }
		| { type: "table"; columns: number; rows: string[][] }
		| { type: "asset"; en?: string; cy?: string }
	)[];
}

export interface Question {
	id: string;
	brand: Brand;
	/**
	 * Reference to the owning Paper (`{brand}--{paperCode}--{season-year}`).
	 * The question inherits the paper's `subject`, `level`, `tier`, `series`
	 * and `date` through this id rather than duplicating them. Required on new
	 * writes; legacy records without it fall back to
	 * {@link getPaperIdFromParentQuestionId}.
	 */
	paperId: string;
	paperCode: string;
	paperCodeAS400?: string;
	language: Language;
	/** Ribbon-aware question identifier (e.g. "1", "9(a)", "5(a)(i)"). */
	questionNumber: string;
	/**
	 * Numeric root extracted from `questionNumber` (e.g. `"9"` for
	 * `"9(c)(ii)"`, `"5"` for `"5"`). Computed at ingest, never
	 * user-edited.
	 */
	rootQuestionNumber: string;
	/**
	 * Deterministic parent-group identifier shared by every item in the
	 * same ribbon group. Format mirrors the seed id but always uses the
	 * root: `{brand}--{paperCode}--{season-year}--{root}`. Computed at
	 * ingest, never user-edited. For a true root, `parentQuestionId`
	 * equals `id` when the seed's deterministic id scheme is in play.
	 */
	parentQuestionId: string;
	/** `true` when `questionNumber === rootQuestionNumber`. */
	isRoot: boolean;
	maxMarks?: number;
	/** 1-based page of the source paper PDF the question was extracted from. Set by PDF import. */
	sourcePage?: number;
	/** 1-based page of the mark scheme PDF holding this question's marking. Set by PDF import. */
	markSchemePage?: number;
	/** Marking read from the mark scheme PDF by PDF import. Reviewer-facing; not rendered to students. */
	markSchemeContent?: ImportedMarkSchemeContent;
	/**
	 * Fixed number of ruled answer lines to render beneath this question in
	 * exported papers. When unset, exports fall back to their own setting
	 * (the default is two lines per mark).
	 */
	answerLines?: number;
	/** English-language authoring content (examiner comments, mark scheme image). */
	en?: QuestionLocalisedContent;
	/** Welsh-language authoring content (examiner comments, mark scheme image). */
	cy?: QuestionLocalisedContent;
	/**
	 * Ordered, shared list of body blocks (prose + images) for the question.
	 * This is the single source of truth for the question body and its ordering.
	 */
	contentBlocks?: QuestionContentBlock[];
	assessmentObjectives?: QuestionAssessmentObjectives;
	specification?: QuestionSpecification;
	/** AI classification proposal awaiting human review. */
	classification?: QuestionClassificationProposal;
	/** Core API reference-data id mappings (QB2-224). */
	coreMappings?: QuestionCoreMappings;
	metadata?: QuestionMetadata;
	status: QuestionStatus;
	deleted?: boolean;
	deletedAt?: string;
	/** ISO-8601 timestamp recording when the question was first created. */
	createdAt?: string;
	/** ISO-8601 timestamp recording the most recent modification. */
	updatedAt?: string;
	/** Identifier of the user (or system actor, e.g. `"seed"`) that created the question. */
	createdBy?: string;
	/** Identifier of the user (or system actor) that performed the most recent modification. */
	updatedBy?: string;
	/**
	 * Who extracted, staged, and published this question, and when.
	 * Server-written. Public reads omit it. See {@link omitReviewProvenance}.
	 */
	reviewProvenance?: QuestionReviewProvenance;
	/**
	 * Why a subject expert sent this question back to draft.
	 * Present only while the question stays in draft after a rejection.
	 * Public reads omit it. See {@link omitInternalReview}.
	 */
	rejectionReason?: string;
	/**
	 * `id` of the parent question in the ribbon hierarchy. For example, the
	 * record with `questionNumber = "10(a)"` references the record with
	 * `questionNumber = "10"` in the same (brand, paperCode, series, level,
	 * tier, language) group. Top-level questions (`"10"`, `"1"`) have no
	 * parent and omit this field.
	 */
	parentId?: string;
}

/** Drop review provenance so a public response cannot carry staff identity. */
export const omitReviewProvenance = <T extends object>(record: T): Omit<T, "reviewProvenance"> => {
	if (!("reviewProvenance" in record)) {
		return record;
	}
	const copy = { ...record };
	delete (copy as { reviewProvenance?: unknown }).reviewProvenance;
	return copy;
};

/** Drop staff review fields so a public response cannot carry them. */
export const omitInternalReview = <T extends object>(record: T): Omit<T, "reviewProvenance" | "rejectionReason"> => {
	if (!("reviewProvenance" in record) && !("rejectionReason" in record)) {
		return record as Omit<T, "reviewProvenance" | "rejectionReason">;
	}
	const copy: Record<string, unknown> = { ...record };
	delete copy.reviewProvenance;
	delete copy.rejectionReason;
	return copy as Omit<T, "reviewProvenance" | "rejectionReason">;
};

export interface QuestionNode extends Question {
	children: QuestionNode[];
}

/**
 * The paper-level facts a question inherits from its Paper. These used to live
 * on the Question; they now have a single source of truth on the Paper (QB2-223)
 * and are merged onto a question only when a consumer needs the full view, via
 * {@link resolveQuestionPaper}.
 */
export interface InheritedPaperFields {
	subject: string | string[];
	qualification: PaperQualification;
	tier?: CoreRef;
	series: QuestionSeries;
	/** ISO-8601 paper date; the single temporal fact search derives year from. */
	date: string;
}

/** A question with its paper-level facts merged in for display or search. */
export type QuestionWithPaper = Question & InheritedPaperFields;

/**
 * Merge a question with the paper-level facts it inherits from its Paper.
 * Pass the resolved Paper (looked up by `question.paperId`). Returns a
 * {@link QuestionWithPaper} carrying both the question's own data and the
 * inherited `subject`/`qualification`/`tier`/`series`/`date`.
 */
export const resolveQuestionPaper = (
	question: Question,
	paper: Pick<Paper, keyof InheritedPaperFields>,
): QuestionWithPaper => ({
	...question,
	subject: paper.subject,
	qualification: paper.qualification,
	tier: paper.tier,
	series: paper.series,
	date: paper.date,
});

export const flattenTree = (nodes: QuestionNode[]): QuestionNode[] =>
	nodes.flatMap((node) => [node, ...flattenTree(node.children ?? [])]);

// ─── API contracts (search) ─────────────────────────────────────────

/**
 * Separator used to pack multiple selected values of the same filter category
 * into a single query-string parameter. ASCII "unit separator" is used instead
 * of a comma because specification theme/topic/subTopic labels contain commas.
 */
export const MULTI_VALUE_DELIMITER = "\u001F";

/**
 * Split a packed multi-value filter parameter into its individual values.
 * Falls back to comma splitting for legacy/hand-written URLs that pre-date
 * {@link MULTI_VALUE_DELIMITER}.
 */
export const splitMultiValue = (raw: string | undefined): string[] => {
	if (!raw) {
		return [];
	}
	const separator = raw.includes(MULTI_VALUE_DELIMITER) ? MULTI_VALUE_DELIMITER : ",";
	return raw
		.split(separator)
		.map((value) => value.trim())
		.filter(Boolean);
};

/**
 * Split a packed multi-value filter parameter using only the canonical
 * delimiter. Use this for categories whose labels can contain commas
 * (for example specification theme/topic/subTopic).
 */
export const splitPackedMultiValue = (raw: string | undefined): string[] => {
	if (!raw) {
		return [];
	}
	return raw
		.split(MULTI_VALUE_DELIMITER)
		.map((value) => value.trim())
		.filter(Boolean);
};

/** Join selected filter values into a single query-string parameter. */
export const joinMultiValue = (values: string[]): string | undefined =>
	values.length === 0 ? undefined : values.join(MULTI_VALUE_DELIMITER);

export interface SearchParams {
	q?: string;
	subject?: string;
	level?: string;
	language?: string;
	tier?: string;
	specification?: string;
	/** Filter by `question.specification.theme` (selects everything under a theme). */
	specificationTheme?: string;
	/** Filter by `question.specification.subTopic`. */
	specificationSubTopic?: string;
	assessmentObjective?: string;
	season?: QuestionSeason;
	year?: number;
	paperCode?: string;
	limit?: number;
	offset?: number;
	status?: QuestionStatus;
	/**
	 * Admin-only: when `true` (and no explicit `status` is supplied), return
	 * questions of every status (draft + published). Public listings omit this
	 * so they default to published-only.
	 */
	includeDrafts?: boolean;
	includeDeleted?: boolean;
}

export interface Aggregation {
	key: string;
	count: number;
}

export interface SpecificationSubTopicAggregation {
	key: string;
	count: number;
}

export interface SpecificationTopicAggregation {
	key: string;
	count: number;
	subTopics: SpecificationSubTopicAggregation[];
}

export interface SpecificationThemeAggregation {
	key: string;
	count: number;
	topics: SpecificationTopicAggregation[];
}

export interface Aggregations {
	subject?: Aggregation[];
	level?: Aggregation[];
	tier?: Aggregation[];
	/**
	 * Hierarchical buckets derived from `question.specification`
	 * (theme → topic → subTopic). Each level carries its own count of
	 * matching questions and the nested children that contributed.
	 */
	specification?: SpecificationThemeAggregation[];
	/** Bucketed by which AO (ao1/ao2/ao3) the question carries marks for. */
	assessmentObjective?: Aggregation[];
	/** Bucketed by the calendar year derived from the owning paper's `date`. Keys are 4-digit year strings. */
	year?: Aggregation[];
	/** Bucketed by `question.paperCode`. */
	paperCode?: Aggregation[];
}

/**
 * Standard search response shape. After QB2's parent/child rework the
 * `/questions` endpoint returns one entry per ribbon root group rather
 * than per individual question, so `data` carries `ParentGroupSummary`
 * records. Use the children endpoint to retrieve a group's actual items.
 */
export interface SearchResponse {
	data: ParentGroupSummary[];
	meta: {
		total: number;
		limit: number;
		offset: number;
	};
	aggregations?: Aggregations;
}

/**
 * Summary record for a parent question group returned by the search
 * endpoint. One per ribbon root in the matching set; sums such as `totalMarks`
 * are aggregated across every item in the group (the parent's own row
 * if `hasOwnRow === true`, plus all ribbon children).`childCount`
 * is the exception.
 */
export interface ParentGroupSummary {
	parentQuestionId: string;
	rootQuestionNumber: string;
	/** Display number for the group — always the root (e.g. `"9"`). */
	questionNumber: string;
	paperCode: string;
	/** Owning paper id — the source of the paper-level fields below (QB2-223). */
	paperId: string;
	/** Paper-level fields, resolved from the owning Paper via `paperId` (QB2-223). */
	series: QuestionSeries;
	subject: string | string[];
	qualification: PaperQualification;
	tier?: CoreRef;
	language: Language;
	brand: Brand;
	/** Publication status of the group's representative (root) question. */
	status: QuestionStatus;
	/** Representative date — resolved from the owning Paper (QB2-223). */
	date: string;
	/** Number of rows in the group with displayable content
	 * A root row that carries its own stem counts
	 */
	childCount: number;
	/** Sum of `maxMarks` across the group. */
	totalMarks: number;
	/**
	 * Representative content blocks for previewing the group — taken from the
	 * root when it carries content, otherwise the first item (by question-number
	 * order) that does. Omitted when no item has content.
	 */
	contentBlocks?: QuestionContentBlock[];
	/**
	 * Representative English-language content for previewing the group (examiner
	 * comments / mark scheme image). Omitted when no item has content.
	 */
	en?: QuestionLocalisedContent;
	/**
	 * Representative Welsh-language content for previewing the group, mirroring
	 * `en`. Omitted when no item has content.
	 */
	cy?: QuestionLocalisedContent;
	/** Deduplicated union of every child's `specification`. */
	specifications: QuestionSpecification[];
	/** Element-wise sum of AO marks across the group; omitted if no item carries AOs. */
	assessmentObjectives?: QuestionAssessmentObjectives;
	/** `true` when the group contains an item with `isRoot === true` (a real parent row). */
	hasOwnRow: boolean;
}

/** Response shape for `GET /questions/parents/{parentQuestionId}/children`. */
export interface ParentChildrenResponse {
	parentQuestionId: string;
	items: QuestionNode[];
}

// ─── Helpers ────────────────────────────────────────────────────────

/**
 * Resolve the ordered content blocks for a question. `contentBlocks` is the
 * single source of truth for the question body; this returns an empty list when
 * none are present.
 */
export const getQuestionContentBlocks = (question: Pick<Question, "contentBlocks">): QuestionContentBlock[] =>
	question.contentBlocks ?? [];

/**
 * If a question part has any content worth showing. Grouped
 * parents that exist only to display their children carry no content,
 * so callers drop them.
 */
export const hasDisplayableContent = (part: Question): boolean =>
	(part.contentBlocks?.length ?? 0) > 0 ||
	[part.en, part.cy].some((content) => !!content && (!!content.markSchemeImage || !!content.examinersComments));

/**
 * Resolve the image asset key for an asset block in a given language, falling
 * back to the other language. Returns `undefined` when the block carries no
 * image.
 */
export const getAssetBlockKey = (block: QuestionAssetBlock, language: Language): string | undefined =>
	language === "cy" ? (block.cy ?? block.en) : (block.en ?? block.cy);

/**
 * CSS width for an asset block from its authored percentage (25/50/75/100).
 * Returns `undefined` when no width is set, so each renderer keeps its own
 * default sizing and existing questions are unaffected.
 */
export const assetWidthCss = (width?: QuestionAssetWidth | number | null): string | undefined =>
	typeof width === "number" && width > 0 ? `${width}%` : undefined;

/**
 * Concatenate the text of a question's content blocks for a given language.
 * Used for previews and full-text search. Returns `undefined` when there is no
 * text for the language.
 */
export const getQuestionContentText = (
	question: Pick<Question, "contentBlocks">,
	language: Language,
): string | undefined => {
	const text = getQuestionContentBlocks(question)
		.filter((block): block is QuestionTextBlock => block.type === "text")
		.map((block) => (language === "cy" ? block.cy : block.en))
		.filter((value): value is string => Boolean(value && value.trim()))
		.join("\n\n");
	return text || undefined;
};

export const getDisplayStatus = (question: Question): QuestionStatus | "deleted" => {
	if (question.deleted) {
		return "deleted";
	}
	return question.status;
};

/** True for a question that is not yet live: draft or staged. Deleted returns false. */
export const isPreLive = (status: QuestionStatus | "deleted"): boolean =>
	status !== "published" && status !== "deleted";

/**
 * Returns a list of human-readable reasons that prevent a question from being
 * published. A question may always be saved as a draft, but it can only be
 * published once every required field is present. The required fields mirror
 * the authoring rules: core metadata, English question content and — for the
 * WJEC brand — the Welsh question content. Examiner comments are optional in
 * both languages.
 *
 * A question that has its own parts (a "grouped parent") is treated the same
 * way whether it is a top-level root (e.g. `1` with parts `1(a)`, `1(b)`) or an
 * intermediate part that itself has sub-parts (e.g. `1(a)` with parts
 * `1(a)(i)`, `1(a)(ii)`). Grouped parents differ from leaf questions:
 * - **Max marks** is required on leaf questions (a part or a standalone root
 *   with no parts). A grouped parent derives its marks from the sum of its
 *   children, so max marks is not required there.
 * - **Mark scheme image(s)** are required on leaf questions. A grouped parent's
 *   mark schemes are broken down per part, so it is not required to carry its
 *   own.
 * - **Question content** is required on leaf questions. A grouped parent holds
 *   its content in those parts, so it isn't required to carry its own question
 *   content.
 * - A grouped parent can only be published once at least one of its parts is
 *   itself published.
 *
 * Pass the question's direct parts via `options.parts` so the
 * leaf-vs-grouped-parent rules can be applied. An empty array means the
 * question is treated as a leaf.
 *
 * Paper-level facts (subject, series, level, paper date) are NOT checked here:
 * they belong to the Paper (QB2-223), are not editable from a question, and are
 * validated when the paper is created, not per question.
 */
export const getPublishBlockers = (
	question: Partial<
		Pick<
			Question,
			"brand" | "paperCode" | "language" | "questionNumber" | "maxMarks" | "isRoot" | "en" | "cy" | "contentBlocks"
		>
	>,
	options?: { parts?: Pick<Question, "status" | "deleted">[] },
): string[] => {
	const blockers: string[] = [];

	// Only live (non-deleted) parts count towards the grouped-parent rules.
	const parts = (options?.parts ?? []).filter((part) => !part.deleted);
	const hasParts = parts.length > 0;
	// Any question that has live parts is a grouped parent — both a top-level
	// root and an intermediate part (e.g. `1(a)` with `1(a)(i)`, `1(a)(ii)`).
	const isGroupedParent = hasParts;

	if (!question.paperCode?.trim()) {
		blockers.push("Paper code is required");
	}
	if (!question.language) {
		blockers.push("Language is required");
	}
	if (!question.questionNumber?.trim()) {
		blockers.push("Question number is required");
	}
	// A grouped parent derives its marks from its children; every leaf question
	// (a part, or a standalone root) must carry an explicit max marks value.
	if (!isGroupedParent && (typeof question.maxMarks !== "number" || question.maxMarks <= 0)) {
		blockers.push("Max marks is required");
	}
	// A grouped parent holds its content in its parts, so it isn't required to
	// carry its own question content. Leaf questions must have English body
	// content (and Welsh for WJEC), authored as ordered blocks.
	if (!isGroupedParent && !getQuestionContentText(question, "en")) {
		blockers.push("Question content (English) is required");
	}
	if (!isGroupedParent && question.brand === "wjec" && !getQuestionContentText(question, "cy")) {
		blockers.push("Question content (Welsh) is required");
	}
	// Mark schemes are required on leaf questions. A grouped parent's mark
	// schemes are broken down per part, so it isn't required to carry its own.
	if (!isGroupedParent) {
		if (!question.en?.markSchemeImage) {
			blockers.push("Mark scheme image (English) is required");
		}
		if (question.brand === "wjec" && !question.cy?.markSchemeImage) {
			blockers.push("Mark scheme image (Welsh) is required");
		}
	}
	// A grouped parent cannot be published until at least one part is live.
	if (isGroupedParent && !parts.some((part) => part.status === "published")) {
		blockers.push("At least one part must be published");
	}
	return blockers;
};

/** Convenience wrapper around {@link getPublishBlockers}. */
export const canPublishQuestion = (
	question: Parameters<typeof getPublishBlockers>[0],
	options?: Parameters<typeof getPublishBlockers>[1],
): boolean => getPublishBlockers(question, options).length === 0;

export interface DryRunPreview {
	questions: DraftQuestionInput[];
	total: number;
}

export interface QuestionChangeRecord {
	questionId: string;
	timestamp: string;
	changes: Array<{ field: string; oldValue: string; newValue: string }>;
	userId: string;
}
